import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Progress,
  Typography,
  Dropdown,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  ScheduleOutlined,
  PrinterOutlined,
  MoreOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import { getAllJobs, getJobsByStage } from "../../api/jobApi";
import JobStatusBadge from "./JobStatusBadge";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const priorityConfig = {
  urgent: { color: "red", label: "Urgent" },
  high: { color: "orange", label: "High" },
  normal: { color: "blue", label: "Normal" },
  low: { color: "default", label: "Low" },
};

const fulfillmentConfig = {
  fulfilled: { color: "success", label: "Fulfilled" },
  partial: { color: "warning", label: "Partial" },
  unfulfilled: { color: "error", label: "Unfulfilled" },
};

const JobList = () => {
  // ── Auth ─────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ────────────────────────────────────────────────
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [filters, setFilters] = useState({
    stage: undefined, // stageCode or "all"
    priority: undefined,
    fulfillment_status: undefined,
  });

  // ── Fetch ────────────────────────────────────────────────
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const apiFilters = {};
      if (filters.priority) apiFilters.priority = filters.priority;
      if (filters.fulfillment_status)
        apiFilters.fulfillment_status = filters.fulfillment_status;
      if (filters.stage && filters.stage !== "all")
        apiFilters.stage = filters.stage;

      const response = await getAllJobs(apiFilters);
      setJobs(response.jobs || []);
      if (response.stages) setStages(response.stages);
    } catch (error) {
      console.log(error.message);
      toast.error("Failed to fetch jobs");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Client-side search (job_number / item_name) ──────────
  const filteredJobs = searchText
    ? jobs.filter(
        (j) =>
          (j.job_number || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (j.item_name || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (j.order?.order_number || "")
            .toLowerCase()
            .includes(searchText.toLowerCase())
      )
    : jobs;

  // ── Action menu per row ──────────────────────────────────
  const getActionMenu = (record) => {
    const items = [
      {
        key: "view",
        label: "View Details",
        icon: <EyeOutlined />,
        onClick: () => navigate(`/job/${record.id}`),
      },
      {
        key: "schedule",
        label: "Schedule",
        icon: <ScheduleOutlined />,
        onClick: () => navigate(`/job/${record.id}?action=schedule`),
      },
      {
        key: "instruction",
        label: "Instructions",
        icon: <PrinterOutlined />,
        onClick: () => navigate(`/job/${record.id}?action=instruction`),
      },
    ];
    return { items };
  };

  // ── Columns ──────────────────────────────────────────────
  const columns = [
    {
      title: "Job #",
      dataIndex: "job_number",
      key: "job_number",
      fixed: "left",
      width: 160,
      render: (text, record) => (
        <Link to={`/job/${record.id}`}>
          <Text strong>{text}</Text>
        </Link>
      ),
    },
    {
      title: "Order",
      key: "order",
      width: 160,
      render: (_, record) =>
        record.order?.order_number ? (
          <Text type="secondary">{record.order.order_number}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
      ellipsis: true,
      width: 200,
    },
    {
      title: "Specs",
      key: "specs",
      width: 170,
      render: (_, record) => (
        <Space size={4} wrap>
          {record.ply && <Tag>{record.ply} Ply</Tag>}
          {record.roll_size && (
            <Tag>RS {record.roll_size}&quot;</Tag>
          )}
          {record.cutting_size && (
            <Tag>CS {record.cutting_size}&quot;</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Quantity",
      key: "quantity",
      width: 160,
      render: (_, record) => {
        const total = record.production_quantity || 0;
        const done = record.completed_quantity || 0;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        return (
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Text>
              {done.toLocaleString()} / {total.toLocaleString()}
            </Text>
            <Progress
              percent={pct}
              size="small"
              showInfo={false}
              status={pct >= 100 ? "success" : "active"}
            />
          </Space>
        );
      },
    },
    {
      title: "Stage",
      key: "stage",
      width: 130,
      render: (_, record) =>
        record.current_stage_name ? (
          <Tag color={record.stage_color || "default"}>
            {record.current_stage_name}
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (priority) => {
        const cfg = priorityConfig[priority] || priorityConfig.normal;
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: "Delivery",
      dataIndex: "expected_delivery",
      key: "expected_delivery",
      width: 120,
      render: (date) =>
        date ? (
          <Tooltip title={moment(date).format("DD MMM YYYY, hh:mm A")}>
            <Text>{moment(date).format("DD MMM YYYY")}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Material",
      dataIndex: "fulfillment_status",
      key: "fulfillment",
      width: 110,
      render: (status) => {
        const cfg = fulfillmentConfig[status] || {
          color: "default",
          label: status || "-",
        };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => <JobStatusBadge status={status} />,
    },
    {
      title: "",
      key: "actions",
      width: 60,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getActionMenu(record)} trigger={["click"]}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────
  return (
    <>
      <PageTitle title="Back" subtitle="Production Jobs" />

      <Card
        title={
          <Space>
            <span>Production Jobs</span>
            <Tag>{filteredJobs.length} jobs</Tag>
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined spin={loading} />}
            onClick={fetchJobs}
          >
            Refresh
          </Button>
        }
      >
        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Search job #, item, order..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(value) => setSearchText(value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Stage"
              allowClear
              style={{ width: "100%" }}
              value={filters.stage}
              onChange={(value) =>
                setFilters((f) => ({ ...f, stage: value }))
              }
            >
              <Option value="all">All Stages</Option>
              {stages.map((s) => (
                <Option key={s.code} value={s.code}>
                  {s.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Priority"
              allowClear
              style={{ width: "100%" }}
              value={filters.priority}
              onChange={(value) =>
                setFilters((f) => ({ ...f, priority: value }))
              }
            >
              <Option value="urgent">Urgent</Option>
              <Option value="high">High</Option>
              <Option value="normal">Normal</Option>
              <Option value="low">Low</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Material Status"
              allowClear
              style={{ width: "100%" }}
              value={filters.fulfillment_status}
              onChange={(value) =>
                setFilters((f) => ({ ...f, fulfillment_status: value }))
              }
            >
              <Option value="fulfilled">Fulfilled</Option>
              <Option value="partial">Partial</Option>
              <Option value="unfulfilled">Unfulfilled</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Button
              onClick={() => {
                setFilters({
                  stage: undefined,
                  priority: undefined,
                  fulfillment_status: undefined,
                });
                setSearchText("");
              }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredJobs}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} jobs`,
          }}
          scroll={{ x: 1400 }}
          size="middle"
        />
      </Card>
    </>
  );
};

export default JobList;
